import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { PackageCheck, RotateCcw, X, Loader2 } from 'lucide-react';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Delivered / Reopen control for a project row in the Client Portfolio.
 * Founder ask Jun 2026: closing a project must stop the day counters,
 * freeze its chat channels and mute its automated nudges.
 */
export default function ProjectDeliverButton({ project, onChanged }) {
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const canClose = ['master_admin', 'production_manager'].includes(user?.role);
  const delivered = project.status === 'delivered' || !!project.delivered_on;

  const deliver = async (e) => {
    e?.stopPropagation();
    setBusy(true); setErr('');
    try {
      await api.post(`/projects/${project.id}/deliver`, { delivered_on: date, note });
      setOpen(false); setNote('');
      onChanged?.();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not mark delivered');
    } finally { setBusy(false); }
  };

  const reopen = async (e) => {
    e?.stopPropagation();
    if (!window.confirm(`Reopen "${project.name}"? Chat unfreezes and reminders resume.`)) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/reopen`, {});
      onChanged?.();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not reopen');
    } finally { setBusy(false); }
  };

  if (delivered) {
    return (
      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
        <span className="px-2 py-0.5 text-[9px] rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 whitespace-nowrap"
          data-testid={`delivered-chip-${project.id}`}>
          <PackageCheck className="w-3 h-3" /> Delivered · {fmtDate(project.delivered_on)}
        </span>
        {canClose && (
          <button onClick={reopen} disabled={busy} data-testid={`reopen-project-${project.id}`}
            className="h-6 px-2 text-[9px] rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center gap-1">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Reopen
          </button>
        )}
      </div>
    );
  }

  if (!canClose) return null;

  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button onClick={() => setOpen(o => !o)} data-testid={`deliver-project-${project.id}`}
        className="h-6 px-2 text-[9px] rounded-sm bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 flex items-center gap-1 whitespace-nowrap">
        <PackageCheck className="w-3 h-3" /> Mark Delivered
      </button>

      {open && (
        <div className="absolute right-0 top-7 z-30 w-72 bg-zinc-950 border border-zinc-800 rounded-md shadow-xl p-3 space-y-2"
          data-testid={`deliver-popover-${project.id}`}>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-white font-semibold">Close “{project.name}”</p>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </div>
          <label className="block">
            <span className="text-[9px] uppercase text-zinc-500 tracking-wider">Delivered on</span>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid={`deliver-date-${project.id}`}
              className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-[11px] text-white outline-none focus:border-emerald-500/60" />
          </label>
          <label className="block">
            <span className="text-[9px] uppercase text-zinc-500 tracking-wider">Note (optional)</span>
            <input value={note} onChange={e => setNote(e.target.value)} data-testid={`deliver-note-${project.id}`}
              placeholder="Final masters handed over" 
              className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-[11px] text-white outline-none" />
          </label>
          <p className="text-[9px] text-zinc-500 leading-snug">
            Day counters stop, the project's chat channels freeze, and automated
            reminders for it go quiet.{project.is_show ? ' Every episode under this show closes too.' : ''} You can reopen any time.
          </p>
          {err && <p className="text-[10px] text-red-400" data-testid={`deliver-error-${project.id}`}>{err}</p>}
          <button onClick={deliver} disabled={busy} data-testid={`deliver-confirm-${project.id}`}
            className="w-full h-8 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-950 text-[11px] font-bold rounded-sm flex items-center justify-center gap-1.5">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />}
            {busy ? 'Closing…' : 'Confirm Delivered'}
          </button>
        </div>
      )}
    </div>
  );
}
